import { FormContent } from "@/components/page-canvas";
import { Form, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { formatLocationTime } from "@/lib/location-time";
import { FormErrors } from "@/components/forms/form-errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  userWebsiteDomainPath,
  userWebsiteDomainVerificationPath,
} from "@/routes";

export type WebsiteDomain = {
  hostname: string;
  status: string;
  checked_at: string | null;
  ownership_verified_at: string | null;
  certificate_expires_at: string | null;
  ownership_name: string;
  ownership_value: string;
  routing_available: boolean;
};

export default function WebsiteDomainSettings({
  domain,
  eligible,
  target,
  returnSection,
}: {
  domain: WebsiteDomain | null;
  eligible: boolean;
  target: string | null;
  returnSection?: "domain";
}) {
  const { t } = useTranslation();
  const { i18n: context, errors } = usePage().props;
  const date = (value: string) =>
    formatLocationTime(value, context, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  return (
    <FormContent>
      <Card>
        <CardHeader>
          <CardTitle>{t("domains.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <FormErrors
            errors={errors}
            targets={{ hostname: "website-hostname" }}
          />
          <p className="text-muted-foreground text-sm">{t("domains.help")}</p>
          {!eligible && <p role="status">{t("domains.plan_required")}</p>}
          {eligible && (
            <Form action={userWebsiteDomainPath()} method="patch">
              {({ processing, errors }) => (
                <div className="space-y-3">
                  {returnSection && (
                    <input
                      type="hidden"
                      name="return_section"
                      value={returnSection}
                    />
                  )}
                  <Label htmlFor="website-hostname">
                    {t("domains.hostname")}
                  </Label>
                  <Input
                    id="website-hostname"
                    name="website_domain[hostname]"
                    defaultValue={domain?.hostname || ""}
                    required
                    maxLength={253}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    aria-invalid={!!errors.hostname}
                    aria-describedby={
                      errors.hostname ? "website-hostname-error" : undefined
                    }
                  />
                  {errors.hostname && (
                    <p
                      id="website-hostname-error"
                      className="text-destructive text-sm"
                    >
                      {errors.hostname}
                    </p>
                  )}
                  <Button type="submit" disabled={processing}>
                    {t("domains.save")}
                  </Button>
                </div>
              )}
            </Form>
          )}
          {domain && (
            <div className="space-y-4">
              <p className="font-medium break-all">{domain.hostname}</p>
              <p role="status">{t(`domains.states.${domain.status}`)}</p>
              {domain.status === "ready" && !domain.routing_available && (
                <p role="status">{t("domains.refresh_required")}</p>
              )}
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="font-medium">{t("domains.txt_name")}</dt>
                  <dd className="mt-1 font-mono break-all select-all">
                    {domain.ownership_name}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">{t("domains.txt_value")}</dt>
                  <dd className="mt-1 font-mono break-all select-all">
                    {domain.ownership_value}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">{t("domains.cname")}</dt>
                  <dd className="mt-1 break-all">
                    {target || t("domains.target_missing")}
                  </dd>
                </div>
              </dl>
              <p className="text-muted-foreground text-sm">
                {t("domains.tls_help")}
              </p>
              {domain.checked_at && (
                <p className="text-sm">
                  {t("domains.checked_at", { date: date(domain.checked_at) })}
                </p>
              )}
              {domain.ownership_verified_at && (
                <p className="text-sm">
                  {t("domains.ownership_at", {
                    date: date(domain.ownership_verified_at),
                  })}
                </p>
              )}
              {domain.certificate_expires_at && (
                <p className="text-sm">
                  {t("domains.certificate_at", {
                    date: date(domain.certificate_expires_at),
                  })}
                </p>
              )}
              <div className="flex flex-wrap gap-3">
                {eligible && (
                  <Form
                    action={userWebsiteDomainVerificationPath()}
                    method="post"
                  >
                    {({ processing }) => (
                      <>
                        {returnSection && (
                          <input
                            type="hidden"
                            name="return_section"
                            value={returnSection}
                          />
                        )}
                        <Button
                          variant="outline"
                          type="submit"
                          disabled={processing}
                        >
                          {t("domains.verify")}
                        </Button>
                      </>
                    )}
                  </Form>
                )}
                <Form action={userWebsiteDomainPath()} method="delete">
                  {({ processing }) => (
                    <>
                      {returnSection && (
                        <input
                          type="hidden"
                          name="return_section"
                          value={returnSection}
                        />
                      )}
                      <Button
                        variant="outline"
                        type="submit"
                        disabled={processing}
                      >
                        {t("domains.remove")}
                      </Button>
                    </>
                  )}
                </Form>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </FormContent>
  );
}
